import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { renderAsync } from "docx-preview";
import { Spin } from "antd";
import { ArrowSquareOut, FileText, WarningCircle } from "@phosphor-icons/react";
import {
  getMediaMimeType,
  getPreviewFileInfo,
  getPresignedDocumentUrl,
} from "../Document/DocumentUtils";
import {
  DOC_FILE_TYPES,
  IMAGE_FILE_TYPES,
  PDF_FILE_TYPES,
} from "../../utils/constants";
import {
  DocxContainer,
  ImagePreview,
  LoadMorePagesButton,
  PageCountLabel,
  ViewerScroll,
  ViewerStatus,
  ViewerStatusDescription,
  ViewerStatusTitle,
  ViewerWrapper,
  ViewerActionButton,
} from "./MobileDocumentViewerStyled";
import MediaPreview from "../Document/MediaPreview";

/**
 * Lightweight document viewer for phone-sized screens (Mobile DMS v1,
 * Phase 3).
 *
 * Deliberately NOT Apryse. The WASM viewer costs 1-3s of bootstrap and
 * tens of MB of memory — acceptable on desktop where the instance is
 * kept alive across row clicks, hostile on phones. This viewer uses
 * the deps the app already ships:
 *
 *   - PDFs    → react-pdf/pdf.js (worker configured in `src/index.js`)
 *   - DOCX    → docx-preview
 *   - images  → plain <img>
 *   - other   → "Open in new tab" handoff to the browser
 *
 * URL sourcing matches the Apryse viewers' contract exactly
 * (`getPresignedDocumentUrl`): presigned S3/CloudFront first, then the
 * authenticated Django stream proxy, then the legacy streaming
 * service. Stream-proxy URLs need cookies (`withCredentials`); direct
 * S3 URLs must NOT send credentials or the S3 CORS response is
 * rejected.
 *
 * Pages render incrementally (PAGE_BATCH at a time) so a 900-page
 * C-file doesn't lock up a phone; VA claim documents regularly run
 * hundreds of pages.
 */

const PAGE_BATCH = 10;

const normalizeType = (fileType) =>
  String(fileType || "")
    .toLowerCase()
    .trim();

const MobileDocumentViewer = ({
  documentId = null,
  fileType = "",
  documentName = "",
}) => {
  const [source, setSource] = useState(null); // { url, isDirect, withCredentials }
  const [sourceError, setSourceError] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [visiblePages, setVisiblePages] = useState(PAGE_BATCH);
  const [pdfError, setPdfError] = useState(false);
  const [docxState, setDocxState] = useState("idle"); // idle | loading | ready | failed
  const [containerWidth, setContainerWidth] = useState(null);
  const scrollRef = useRef(null);
  const docxRef = useRef(null);

  const kind = useMemo(() => {
    const normalized = normalizeType(fileType);
    const classified = getPreviewFileInfo({
      fileName: source?.fileName || fileType || documentName,
      contentType: source?.contentType,
    }).fileType;
    if (classified === "image" || IMAGE_FILE_TYPES.includes(normalized)) return "image";
    if (classified === "audio" || classified === "video") return classified;
    if (DOC_FILE_TYPES.includes(normalized)) return "docx";
    if (PDF_FILE_TYPES.includes(normalized)) return "pdf";
    if (!normalized) {
      // Inbox mail documents carry no file_type but are guaranteed PDF
      // by the storage contract (see InboxDocumentViewer's extension
      // pin). Unknown-but-named types fall through to "other".
      return "pdf";
    }
    return "other";
  }, [documentName, fileType, source?.contentType, source?.fileName]);

  // Track the scroller's width so PDF pages render at exactly the
  // viewport width (retina-sharp, no horizontal scroll).
  useEffect(() => {
    const node = scrollRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width;
      // 16px = ViewerScroll's horizontal padding on both sides.
      if (width) setContainerWidth(Math.max(100, Math.floor(width - 16)));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [source]);

  // Resolve the document URL whenever the document changes.
  useEffect(() => {
    if (!documentId) {
      setSource(null);
      return undefined;
    }
    let cancelled = false;
    setSource(null);
    setSourceError(false);
    setNumPages(null);
    setVisiblePages(PAGE_BATCH);
    setPdfError(false);
    setDocxState("idle");
    (async () => {
      try {
        const resolved = await getPresignedDocumentUrl(documentId);
        if (cancelled) return;
        if (resolved?.url) {
          setSource(resolved);
        } else {
          setSourceError(true);
        }
      } catch {
        if (!cancelled) setSourceError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [documentId]);

  // DOCX path: fetch bytes and hand them to docx-preview.
  useEffect(() => {
    if (kind !== "docx" || !source?.url || !docxRef.current) return undefined;
    let cancelled = false;
    setDocxState("loading");
    (async () => {
      try {
        const response = await fetch(source.url, {
          credentials: source.withCredentials ? "include" : "omit",
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const buffer = await response.arrayBuffer();
        if (cancelled || !docxRef.current) return;
        docxRef.current.innerHTML = "";
        await renderAsync(buffer, docxRef.current, undefined, {
          inWrapper: true,
          breakPages: true,
          useBase64URL: true,
        });
        if (!cancelled) setDocxState("ready");
      } catch (err) {
        console.error("Mobile DOCX render failed:", err);
        if (!cancelled) setDocxState("failed");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [kind, source]);

  const pdfFile = useMemo(() => {
    if (!source?.url) return null;
    return source.withCredentials
      ? { url: source.url, withCredentials: true }
      : { url: source.url };
  }, [source]);

  const handlePdfLoad = useCallback(({ numPages: total }) => {
    setNumPages(total);
  }, []);

  const handleOpenInTab = useCallback(() => {
    if (source?.url) {
      window.open(source.url, "_blank", "noopener");
    }
  }, [source]);

  if (!documentId) {
    return (
      <ViewerWrapper>
        <ViewerStatus role="status">
          <FileText size={36} weight="duotone" color="#98A6BA" />
          <ViewerStatusTitle>No document selected</ViewerStatusTitle>
        </ViewerStatus>
      </ViewerWrapper>
    );
  }

  if (sourceError) {
    return (
      <ViewerWrapper>
        <ViewerStatus role="alert">
          <WarningCircle size={36} weight="duotone" color="#A61412" />
          <ViewerStatusTitle>Couldn&apos;t load this document</ViewerStatusTitle>
          <ViewerStatusDescription>
            The document URL couldn&apos;t be resolved. Check your connection
            and try again.
          </ViewerStatusDescription>
        </ViewerStatus>
      </ViewerWrapper>
    );
  }

  if (!source) {
    return (
      <ViewerWrapper>
        <ViewerStatus role="status">
          <Spin />
          <ViewerStatusDescription>
            Loading {documentName || "document"}…
          </ViewerStatusDescription>
        </ViewerStatus>
      </ViewerWrapper>
    );
  }

  if (kind === "image") {
    return (
      <ViewerWrapper>
        <ViewerScroll ref={scrollRef}>
          <ImagePreview
            src={source.url}
            alt={source.fileName || documentName || "Document"}
          />
        </ViewerScroll>
      </ViewerWrapper>
    );
  }

  if (kind === "audio" || kind === "video") {
    return (
      <ViewerWrapper>
        <MediaPreview
          kind={kind}
          src={source.url}
          fileName={source.fileName || documentName || "Media file"}
          mimeType={
            source.contentType
            || getMediaMimeType(source.fileName || documentName)
          }
          withCredentials={source.withCredentials}
        />
      </ViewerWrapper>
    );
  }

  if (kind === "docx") {
    return (
      <ViewerWrapper>
        <ViewerScroll ref={scrollRef}>
          {docxState === "loading" && (
            <ViewerStatus role="status">
              <Spin />
            </ViewerStatus>
          )}
          {docxState === "failed" && (
            <ViewerStatus role="alert">
              <WarningCircle size={32} weight="duotone" color="#A61412" />
              <ViewerStatusTitle>Preview unavailable</ViewerStatusTitle>
              <ViewerActionButton type="button" onClick={handleOpenInTab}>
                <ArrowSquareOut size={14} weight="bold" />
                Open in new tab
              </ViewerActionButton>
            </ViewerStatus>
          )}
          <DocxContainer
            ref={docxRef}
            style={{ display: docxState === "ready" ? "block" : "none" }}
          />
        </ViewerScroll>
      </ViewerWrapper>
    );
  }

  if (kind === "other") {
    return (
      <ViewerWrapper>
        <ViewerStatus role="status">
          <FileText size={36} weight="duotone" color="#98A6BA" />
          <ViewerStatusTitle>
            {documentName || "This document"}
          </ViewerStatusTitle>
          <ViewerStatusDescription>
            This file type doesn&apos;t have an in-app phone preview yet.
          </ViewerStatusDescription>
          <ViewerActionButton type="button" onClick={handleOpenInTab}>
            <ArrowSquareOut size={14} weight="bold" />
            Open in new tab
          </ViewerActionButton>
        </ViewerStatus>
      </ViewerWrapper>
    );
  }

  // PDF (default)
  if (pdfError) {
    return (
      <ViewerWrapper>
        <ViewerStatus role="alert">
          <WarningCircle size={36} weight="duotone" color="#A61412" />
          <ViewerStatusTitle>Couldn&apos;t render this PDF</ViewerStatusTitle>
          <ViewerActionButton type="button" onClick={handleOpenInTab}>
            <ArrowSquareOut size={14} weight="bold" />
            Open in new tab
          </ViewerActionButton>
        </ViewerStatus>
      </ViewerWrapper>
    );
  }

  const pagesToRender = numPages
    ? Math.min(visiblePages, numPages)
    : 0;

  return (
    <ViewerWrapper>
      <ViewerScroll ref={scrollRef}>
        <Document
          file={pdfFile}
          onLoadSuccess={handlePdfLoad}
          onLoadError={(err) => {
            console.error("Mobile PDF load failed:", err);
            setPdfError(true);
          }}
          loading={
            <ViewerStatus role="status">
              <Spin />
            </ViewerStatus>
          }
        >
          {Array.from({ length: pagesToRender }, (_, idx) => (
            <Page
              key={`page-${idx + 1}`}
              pageNumber={idx + 1}
              width={containerWidth || undefined}
              renderAnnotationLayer={false}
              renderTextLayer={false}
            />
          ))}
        </Document>
        {numPages != null && (
          <PageCountLabel aria-live="polite">
            Showing {pagesToRender} of {numPages} page
            {numPages === 1 ? "" : "s"}
          </PageCountLabel>
        )}
        {numPages != null && visiblePages < numPages && (
          <LoadMorePagesButton
            type="button"
            onClick={() =>
              setVisiblePages((prev) => prev + PAGE_BATCH)
            }
          >
            Load {Math.min(PAGE_BATCH, numPages - visiblePages)} more page
            {Math.min(PAGE_BATCH, numPages - visiblePages) === 1 ? "" : "s"}
          </LoadMorePagesButton>
        )}
      </ViewerScroll>
    </ViewerWrapper>
  );
};

MobileDocumentViewer.propTypes = {
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fileType: PropTypes.string,
  documentName: PropTypes.string,
};

export default MobileDocumentViewer;
